import React from 'react';
import { Helmet } from 'react-helmet';
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from './config';

function toAbs(url) {
    if (!url) return SITE_URL + '/';
    return url.startsWith('http') ? url : `${SITE_URL}${url}`;
}

export default function SEO({
    title = SITE_NAME,
    description = '',
    keywords = [],
    robots = 'index, follow',
    canonical = '/',
    image = DEFAULT_OG_IMAGE,
    type = 'website',
    structuredData = []
}) {
    const keywordsStr = Array.isArray(keywords) ? keywords.join(', ') : keywords;
    const canonicalUrl = toAbs(canonical);
    const sd = Array.isArray(structuredData) ? structuredData : (structuredData ? [structuredData] : []);

    return (
        <Helmet>
            <title>{title}</title>

            {/* Basic */}
            <meta name="description" content={description} />
            <meta name="keywords" content={keywordsStr} />
            <meta name="robots" content={robots} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph */}
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={toAbs(image)} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={toAbs(image)} />

            {/* JSON-LD */}
            {sd.map((obj, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(obj)}
                </script>
            ))}
        </Helmet>
    );
}

/* Admin (dashboard) */
export const ADMIN_PAGE_META = {
    '/': {
        title: 'Admin Dashboard | Fuel Clubs',
        description:
            'Monitor KPIs, today’s routes, upcoming appointments, invoices, and team activity to run Fuel Clubs efficiently.',
    },

    '/appointments': {
        title: 'Appointments | Fuel Clubs',
        description:
            'Browse, filter, and manage fuel delivery appointments. Track status, reschedule, or assign drivers.',
    },

    '/add-appointment': {
        title: 'Create Appointment | Fuel Clubs',
        description:
            'Schedule a new fuel delivery: select client, vehicles, service date/time, and notes.',
    },

    // Dynamic builders
    '/add-appointment/:clientID': ({ clientID }) => ({
        title: `Create Appointment | Fuel Clubs`,
        description:
            'Start a new delivery for a selected client. Prefilled details for faster booking.',
    }),

    '/edit-appointment/:id': ({ id }) => ({
        title: `Edit Appointment | Fuel Clubs`,
        description:
            'Update delivery time, vehicle, status, or notes for this appointment.',
    }),

    '/view-appointment/:tab/:id': ({ id, tab }) => ({
        title: `Appointment | ${humanizeTab(tab)} | Fuel Clubs`,
        description:
            `View appointment details, timeline, messages, and invoices on the “${humanizeTab(tab)}” tab.`,
    }),

    '/clients': {
        title: 'Clients | Fuel Clubs',
        description:
            'Manage customer profiles, vehicles, service history, and membership status.',
    },

    '/client-detail/:tab/:id': ({ id, tab }) => ({
        title: `Client | ${humanizeTab(tab)} | Fuel Clubs`,
        description:
            `View client info, vehicles, bookings, invoices, and messages on the “${humanizeTab(tab)}” tab.`,
    }),

    '/add-client': {
        title: 'Add Client | Fuel Clubs',
        description:
            'Create a new customer profile with contact details, vehicles, and preferences.',
    },

    '/edit-client/:id': ({ id }) => ({
        title: `Edit Client | Fuel Clubs`,
        description:
            'Update customer information, vehicles, membership, and preferences.',
    }),

    '/invoices': {
        title: 'Invoices | Fuel Clubs',
        description:
            'Generate, filter, and manage invoices. Track payments, status, and export records.',
    },

    '/add-invoice': {
        title: 'Create Invoice | Fuel Clubs',
        description:
            'Issue a new invoice: add line items, taxes/fees, and send to the customer.',
    },

    '/add-invoice/:clientID': ({ clientID }) => ({
        title: `Create Invoice | Fuel Clubs`,
        description:
            'Prepare an invoice prefilled for the selected client.',
    }),

    '/edit-invoice/:id': ({ id }) => ({
        title: `Edit Invoice | Fuel Clubs`,
        description:
            'Modify invoice line items, notes, and payment status.',
    }),

    '/view-invoice/:id': ({ id }) => ({
        title: `Invoice | Fuel Clubs`,
        description:
            'View or download the invoice, check payment history, and share with the customer.',
    }),

    '/availability': {
        title: 'Availability | Fuel Clubs',
        description:
            'Set service hours, blackout dates, and driver availability to optimize scheduling.',
    },

    '/messages': {
        title: 'Messages | Fuel Clubs',
        description:
            'Handle customer and driver conversations. Search threads and send updates.',
    },

    '/messages/:userId': ({ userId }) => ({
        title: `Messages | Fuel Clubs`,
        description:
            'Chat with a customer or teammate about orders, scheduling, or billing.',
    }),

    '/routes': {
        title: 'Routes & Dispatch | Fuel Clubs',
        description:
            'Plan and optimize delivery routes, assign drivers, and monitor progress in real time.',
    },

    '/team': {
        title: 'Team | Fuel Clubs',
        description:
            'Manage team members, roles, and permissions for operations at Fuel Clubs.',
    },

    '/information': {
        title: 'Information | Fuel Clubs',
        description:
            'Company information, documents, legal references, and internal notes.',
    },

    '/settings/:tab': ({ tab }) => ({
        title: `Settings | ${humanizeTab(tab)} | Fuel Clubs`,
        description:
            `Configure company profile, billing, notifications, integrations, and more under “${humanizeTab(tab)}”.`,
    }),

    // Often mapped to Dashboard
    '*': {
        title: 'Admin Dashboard | Fuel Clubs',
        description:
            'Monitor KPIs, routes, appointments, invoices, and team activity.',
    },

    // Included in your routes|kept consistent with public version
    '/contact-us': {
        title: 'Contact Fuel Clubs | Support',
        description:
            'Get help with memberships, scheduling, billing, or technical support. Our team is here to help.',
    },
};

/* Customer (dashboard) */
export const CUSTOMER_PAGE_META = {
    '/': {
        title: 'Dashboard | Fuel Clubs',
        description:
            'See upcoming deliveries, recent invoices, messages, and membership status at a glance.',
    },

    '/dashboard': {
        title: 'Dashboard | Fuel Clubs',
        description:
            'Overview of your upcoming fuel deliveries, invoices, and messages.',
    },

    '/messages': {
        title: 'Messages | Fuel Clubs',
        description:
            'Chat with support or your driver. Track conversations about deliveries and billing.',
    },

    '/messages/:userId': ({ userId }) => ({
        title: `Messages with ${userId} | Fuel Clubs`,
        description:
            'Continue your conversation about deliveries, routing, or billing.',
    }),

    '/appointments': {
        title: 'Appointments | Fuel Clubs',
        description:
            'View, schedule, reschedule, or cancel your fuel delivery appointments.',
    },

    '/invoices': {
        title: 'Invoices | Fuel Clubs',
        description:
            'Review, download, or pay invoices for your deliveries.',
    },

    '/view-invoice/:id': ({ id }) => ({
        title: `Invoice | Fuel Clubs`,
        description:
            'See invoice details, line items, payments, and receipt.',
    }),

    '/services': {
        title: 'Services | Fuel Clubs',
        description:
            'Browse fuel delivery options and membership benefits for your vehicles.',
    },

    '/add-appointment': {
        title: 'Schedule Delivery | Fuel Clubs',
        description:
            'Choose vehicle, location, and time to book a fuel delivery.',
    },

    '/settings/:tab': ({ tab }) => ({
        title: `Settings | ${humanizeTab(tab)} | Fuel Clubs`,
        description:
            `Update profile, vehicles, addresses, notifications, and security in “${humanizeTab(tab)}”.`,
    }),

    '*': {
        title: 'Dashboard | Fuel Clubs',
        description:
            'Overview of your upcoming fuel deliveries, invoices, and messages.',
    },

    '/contact-us': {
        title: 'Contact Fuel Clubs | Support',
        description:
            'Questions about scheduling, billing, or membership? Contact our support team.',
    },

    // Onboarding (also present in NotOnboardedRoutes)
    '/onboard/membership': {
        title: 'Choose Membership | Fuel Clubs',
        description:
            'Pick the plan that fits your needs and start scheduling fuel deliveries.',
    },

    '/onboard/customer': {
        title: 'Create Profile | Fuel Clubs',
        description:
            'Set up your profile with contact details, vehicles, and addresses to begin.',
    },
};

/* Helpers */
export function humanizeTab(tab) {
    if (!tab) return 'Overview';
    return String(tab)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase()); // title-case
}